import { Component } from '@theme/component';

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;
  #motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  #resizeObserver;
  #marqueeOriginals;

  #motionChanged = () => {
    this.suspend();
    if (this.hasAttribute('marquee-mode')) this.#initMarquee();
    else this.play();
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.suspend();
    this.#resizeObserver?.disconnect();
    this.#motion.removeEventListener('change', this.#motionChanged);
    this.removeEventListener('mouseenter', this.suspend);
    this.removeEventListener('mouseleave', this.resume);
    this.removeEventListener('focusin', this.suspend);
    this.removeEventListener('focusout', this.resume);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
  }

  toggleMotion = () => {
    const paused = !this.hasAttribute('motion-paused');
    this.toggleAttribute('motion-paused', paused);
    const button = this.querySelector('.announcement-bar__pause');
    button?.setAttribute('aria-pressed', String(paused));
    if (!this.hasAttribute('marquee-mode')) {
      if (paused) this.pause();
      else this.play();
    }
  };


  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  connectedCallback() {
    super.connectedCallback();
    this.#motion.addEventListener('change', this.#motionChanged);


    if (this.hasAttribute('marquee-mode')) {
      this.#initMarquee();
      this.#resizeObserver = new ResizeObserver(() => this.#initMarquee());
      this.#resizeObserver.observe(this);
      document.fonts.ready.then(() => {
        if (this.isConnected) this.#initMarquee();
      });
      return;
    }

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    this.addEventListener('focusin', this.suspend);
    this.addEventListener('focusout', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.play();
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay || this.#motion.matches || this.hasAttribute('motion-paused')) return;
    this.suspend();

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover, :focus-within') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.#current = current;

    let relativeIndex = current % (this.refs.slides ?? []).length;
    if (relativeIndex < 0) {
      relativeIndex += (this.refs.slides ?? []).length;
    }

    this.refs.slides?.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== relativeIndex}`);
    });
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());

  #initMarquee() {
    const track = this.querySelector('.announcement-bar__marquee-track');
    if (!(track instanceof HTMLElement)) return;
    this.style.setProperty('--announcement-marquee-direction', this.getAttribute('direction') === 'right' ? 'reverse' : 'normal');
    if (!this.#marqueeOriginals) this.#marqueeOriginals = Array.from(track.children);
    if (!this.#marqueeOriginals.length) return;
    const originalGroup = document.createElement('div');
    originalGroup.className = 'announcement-bar__marquee-group';
    originalGroup.append(...this.#marqueeOriginals);
    track.replaceChildren(originalGroup);
    if (this.#motion.matches) return;

    const duplicate = (element) => {
      const clone = element.cloneNode(true);
      clone.setAttribute('data-marquee-clone', '');
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('inert', '');
      [clone, ...clone.querySelectorAll('*')].forEach((node) => {
        node.removeAttribute('id');
        node.removeAttribute('ref');
        node.removeAttribute('data-shopify-editor-block');
      });
      return clone;
    };
    // Two equal-width groups make the -50% wrap exact at every viewport width.
    for (let repeat = 0; originalGroup.getBoundingClientRect().width < this.clientWidth && repeat < 20; repeat++) {
      this.#marqueeOriginals.forEach((item) => originalGroup.append(duplicate(item)));
    }
    track.append(duplicate(originalGroup));
  }

}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
